import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { type UpdateEmployeeRequestDto } from '@/api/generated/models/update-employee-request-dto';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { useCompanyApiClient } from '@/api/util/useCompanyApiClient.util.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useTypedParams } from '@/hooks/useTypedParams.hook.ts';
import { RouteEnum } from '@/layouts/routes/Route.model.ts';

type Params = {
  employeeId?: string;
  myProfileView: boolean;
  onSuccess: () => Promise<void>;
};

type Return = {
  isError: boolean;
  isSubmitting: boolean;
  submitCompanyEmployee: (values: UpdateEmployeeRequestDto) => Promise<void>;
};

export const useSubmitCompanyEmployee = (params: Params): Return => {
  const { companyId } = useTypedParams(RouteEnum.COMPANY_EMPLOYEE);
  const { user } = useUserContext();
  const { usersCompanyDataApi } = useApiClient();
  const { companyEmployeesApi } = useCompanyApiClient();
  const { employeeId, myProfileView, onSuccess } = params;
  const queryClient = useQueryClient();

  const { isError, isPending, mutateAsync } = useMutation<
    void,
    AxiosError,
    UpdateEmployeeRequestDto
  >({
    mutationFn: async values => {
      if (!user.userId) {
        return;
      }
      if (myProfileView) {
        await usersCompanyDataApi.updateCompanyEmployee(user.userId, values);
        await queryClient.refetchQueries({ queryKey: [QueryKeyEnum.CURRENT_USER] });
      } else {
        if (!companyId || !employeeId) {
          return;
        }
        await companyEmployeesApi.updateCompanyEmployee(companyId, employeeId, values);
      }
      await onSuccess();
    },
  });

  return {
    isError: isError,
    isSubmitting: isPending,
    submitCompanyEmployee: mutateAsync,
  };
};
